import React, { Fragment, useState } from 'react';
import styled from '@emotion/styled';

import { CheckoutContext, useCheckoutContext } from '../../../contexts/Checkout';

import PlaylistAddCheckCircleRoundedIcon from '@mui/icons-material/PlaylistAddCheckCircleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import Person3RoundedIcon from '@mui/icons-material/Person3Rounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';

import { BasicFlex, ScrollableContainer } from '../../../utils/Common';

import BasicSideDrawer from '../../../utils/utilities/SideDrawer';

import NoItems from "../../../utils/NoItems";

import { formatGenericDate, formatGenericTime, formatCurrency } from "../../../utils/util";

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';
import SimpleChip from '../../../utils/utilities/SimpleChip';

import { useQuery } from '@tanstack/react-query';

import { getAppointmentsForCheckout } from '../../Appointments/apiService';

import ProfileBar from '../../../utils/utilities/ProfileBar';

import FullDialog from '../../../utils/utilities/Dialog';

import EditCheckout from '../../Checkout/EditCheckout';

import { Tooltip, IconButton, Badge, useTheme, alpha, Button, Card, CardHeader, CardContent, CardActions, Divider } from '@mui/material';

const NoItemsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50%;
  border: 2px dashed ${props => props.theme.colors.border};
  padding: .6rem;
  border-radius: 8px;
  margin: 1rem 0;
` 

const AppointmentList = styled(ScrollableContainer)`
  border: none;
  border-radius: 8px;
  padding: .8rem .5rem;
  background-color: transparent;
  min-height: 85vh;
  max-height: 85vh;
  /* margin-top: 1rem; */
`

const AppointmentCard = ({ event = {}, setExpand }) => {

  const { appointment, setAppointment } = useCheckoutContext();

  const moveToCheckout = () => {
    setAppointment(event);
    setExpand(false);
  }

  const basicBlock = [
    {header: "", icon: <TodayRoundedIcon fontSize="small" />, info: `${formatGenericTime(event.start_time)} - ${formatGenericTime(event.end_time)}`, spread: "2rem"},
    {header: "", icon: <Person3RoundedIcon fontSize="small" />, info: <SimpleChip label={event.stylist} />, spread: "2rem"},
    {header: "", icon: <AccessTimeRoundedIcon fontSize="small" />, info: `${event.duration_in_minutes} minutes`, spread: "2rem"},
    {header: "", icon: <PaidRoundedIcon fontSize="small" />, info: formatCurrency(event.subtotal), spread: "2rem"}
  ]

  return (
    <Card sx={{p: 0, width: "100%"}} variant="outlined">
      <ProfileBar name={event.customer || "John Doe"} phone={event.customer_phone || "123-456-7890"} />
      <Divider />
      <CardHeader 
        title={<BasicFlex gap="1rem" justify="flex-start"><AutoFixHighRoundedIcon fontSize="small" /> {event.title}</BasicFlex>} 
        subheader={`${formatGenericDate(event.appointment_date)} - ${formatGenericTime(event.start_time)}`} slotProps={{title: {sx: {fontSize: "1.6rem"}}}} 
        action={<MultiStatusChip value={event.status} values={[{value: "scheduled", label: "Scheduled", status: "primary"}, {value:     "in_progress", label: "In Progress", status: "warning"}, {value: "completed", label: "Show", status: "success"}, {value: "no_show", status: "error"}, {value: "cancelled", label: "Cancelled", status: "error"}]} />} 
      />
      <CardContent>

        <CardBlockContainer>
          {basicBlock.map((block, i) => <CardBlock key={i} header={block.header} icon={block.icon} info={block.info} spread={block.spread} />)}
        </CardBlockContainer>

      </CardContent>
      <Divider />
      <CardActions>
        <Button fullWidth startIcon={<ShoppingCartCheckoutRoundedIcon />} variant="contained" onClick={moveToCheckout}>Proceed To Checkout</Button>
      </CardActions>
    </Card>
  )

}

const SideDrawer = ({ expand, setExpand }) => {
  const { appointment, setAppointment } = useCheckoutContext();
  
  const { data: appointments } = useQuery({
    queryKey: ['appointments', 'ready'],
    queryFn: () => getAppointmentsForCheckout(),
    enabled: expand
  })

  return (
    <BasicSideDrawer open={expand} title="Appointment Checkout" handleClose={() => {
      setExpand(false)
      setAppointment(null)
    }}>
      {appointments?.length ? (
        <AppointmentList>
          <BasicFlex dir="column" gap="1rem">
            {appointments?.map(appt => <AppointmentCard key={appt.service_ticket_id} event={appt} setExpand={setExpand} />)}
          </BasicFlex>
        </AppointmentList>) : <NoItemsContainer><NoItems title="No Appointments to Checkout" /></NoItemsContainer>}

    </BasicSideDrawer>
  )
}

const ReadyForPickup = () => {
  const [expand, setExpand] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const theme = useTheme();
  return (
    <CheckoutContext.Provider value={{ appointment, setAppointment }} >
      <Tooltip title="Checkout">
        <IconButton onClick={() => setExpand(true)} color="success" sx={{backgroundColor: alpha(theme.palette.success.main, 0.2)}}>
          <Badge color="error" badgeContent={0}>
            <ShoppingCartCheckoutRoundedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <SideDrawer expand={expand} setExpand={setExpand} />
      {appointment?.appointment_id ? <FullDialog fullScreen forceOpen={appointment?.appointment_id ? true : false} title="Appointment Checkout" useForm={true} 
      forceClose={() => setAppointment(null)} 
      >
        <EditCheckout />
      </FullDialog> : null}
    </CheckoutContext.Provider>
  )
};

export default ReadyForPickup;